<template>
  <v-menu>
    <template #activator="{ props }">
      <v-tooltip v-bind="props" location="bottom">
        <template #activator="{ props: tooltip }">
          <div v-bind="tooltip">
            <v-btn-icon
              icon="mdi-download"
              v-bind="props"
              :class="[{ 'px-5': $vuetify.display.sm }, { 'px-4': $vuetify.display.xs }]"
              data-testid="downloadItem.menu-button"
            >
            </v-btn-icon>
          </div>
        </template>
        <span>Export Statechart</span>
      </v-tooltip>
    </template>
    <v-list density="compact">
      <v-list-item @click="exportJson()">
        <v-list-item-title>Export Statechart</v-list-item-title>
      </v-list-item>
      <v-list-item data-testid="downloadItem.svg-button" @click="exportSvg()">
        <v-list-item-title>Export as SVG</v-list-item-title>
      </v-list-item>
      <v-list-item data-testid="downloadItem.png-button" @click="exportPng()">
        <v-list-item-title>Export as PNG</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script setup lang="ts">
import { MessageEventBus, SimulationEventBus } from "../../../event-bus";
import { ErrorsList } from "../../mvc/ErrorsList";
import { WarningsList } from "../../mvc/WarningsList";
import RappidContext from "../../rappid/rappid-context";
import ModelLoader from "../../services/model-loader";
import { ImageExporter } from "../../services/transformers/image-exporter";
import { BaseView } from "../../mvc/base/base-node";
import { TransitionView } from "../../mvc/transition";
import { LinkWarningsList } from "../../mvc/LinkWarningsList";
import { LinkErrorList } from "../../mvc/LinkErrorsList";
import { inject } from "vue";
import { formatExportFileName } from "../../utils/exportFileName";

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const modelLoader: ModelLoader = inject<ModelLoader>("modelLoader");

const compProps = defineProps<{ statechartName: string }>();

function exportSvg() {
  SimulationEventBus.emit("stopSimulation");
  hideMarkers();
  new ImageExporter().exportSvg(rappidContext.paper, rappidContext.paperScroller, getExportFileName());
  showMarkers();
}

function exportPng() {
  SimulationEventBus.emit("stopSimulation");
  hideMarkers();
  new ImageExporter().exportPng(rappidContext.paper, rappidContext.paperScroller, getExportFileName());
  showMarkers();
}

function hideMarkers() {
  ErrorsList.removeAll(rappidContext.paper);
  WarningsList.removeAll(rappidContext.paper);
  LinkWarningsList.removeAll(rappidContext.paper);
  LinkErrorList.removeAll(rappidContext.paper);
}

function showMarkers() {
  rappidContext.graph.getCells().forEach((cell) => {
    const view = cell.findView(rappidContext.paper);
    if (view instanceof TransitionView || view instanceof BaseView) {
      view.displayDiagnostics();
    }
  });
}

function showSuccess() {
  MessageEventBus.emit("success", {
    text: "Export successful!",
    id: "download-item",
  });
}

function exportJson() {
  const exportTitle = compProps.statechartName + " Export";
  const graph = JSON.parse(JSON.stringify(rappidContext.paper.model));
  graph.cells[0].attrs.name.text = exportTitle;
  const json = modelLoader.saveModelToText(graph);
  const fileName = formatExportFileName(exportTitle) + ".scm";
  const blob = new Blob([json], { type: "application/json" });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  showSuccess();
}

function getExportFileName(): string {
  return formatExportFileName(compProps.statechartName + " Export");
}
</script>

<style scoped></style>
